import { startOfDay, isBefore, isSameDay, parseISO } from 'date-fns'
import type { Bloco } from '@/types/bloco'

export type BlocoSectionKey = 'awaitingYou' | 'overdue' | 'today' | 'notes' | 'done' | 'history'

export type BlocosTab = 'active' | 'history'

export interface BlocoSection {
  key: BlocoSectionKey
  title: string
  items: Bloco[]
}

function parseDueDate(value: string | null): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function groupActiveBlocos(blocos: Bloco[], currentUserId?: string): BlocoSection[] {
  const todayStart = startOfDay(new Date())
  const open = blocos.filter((b) => b.status === 'open')

  const awaitingYou: Bloco[] = []
  const overdue: Bloco[] = []
  const today: Bloco[] = []
  const notes: Bloco[] = []

  for (const bloco of open) {
    if (currentUserId && bloco.pendingUserId === currentUserId) {
      awaitingYou.push(bloco)
      continue
    }

    const due = parseDueDate(bloco.dueAt)
    if (bloco.type === 'return' && due) {
      if (isBefore(due, todayStart)) {
        overdue.push(bloco)
        continue
      }
      if (isSameDay(due, todayStart)) {
        today.push(bloco)
        continue
      }
    }
    notes.push(bloco)
  }

  const sections: BlocoSection[] = []
  if (awaitingYou.length) {
    sections.push({ key: 'awaitingYou', title: 'Aguardando sua resposta', items: awaitingYou })
  }
  if (overdue.length) sections.push({ key: 'overdue', title: 'Atrasados', items: overdue })
  if (today.length) sections.push({ key: 'today', title: 'Hoje', items: today })
  if (notes.length) sections.push({ key: 'notes', title: 'Notas', items: notes })

  return sections
}

export function groupHistoryBlocos(blocos: Bloco[]): BlocoSection[] {
  const done = blocos
    .filter((b) => b.status === 'done')
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

  if (!done.length) return []
  return [{ key: 'history', title: 'Histórico', items: done }]
}

/** @deprecated Use groupActiveBlocos or groupHistoryBlocos */
export function groupBlocos(blocos: Bloco[], currentUserId?: string): BlocoSection[] {
  return [...groupActiveBlocos(blocos, currentUserId), ...groupHistoryBlocos(blocos)]
}

export function formatBlocoDueLabel(bloco: Bloco): string | null {
  const due = parseDueDate(bloco.dueAt)
  if (!due) return null
  const dateLabel = due.toLocaleDateString('pt-BR')
  return bloco.dueTime ? `${dateLabel} às ${bloco.dueTime}` : dateLabel
}

export function formatMemberStatusLabel(status: 'open' | 'done'): string {
  return status === 'done' ? 'Concluído' : 'Pendente'
}

export function summarizeSharedMemberStatus(bloco: Bloco): string | null {
  if (!bloco.isShared && (bloco.members?.length ?? 0) <= 1) return null
  const members = bloco.members ?? []
  const openCount = members.filter((member) => member.status === 'open').length
  const doneCount = members.length - openCount
  return `${doneCount}/${members.length} concluíram`
}
